import * as qs from 'query-string';
import * as _ from 'lodash';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, SwapQualityIssueActionCreators, ISwapQualityIssueResponse, ISwapQualityLookupDataResponse, ISwapQualityIssueRequest } from '@nkr/store/index';
import { IBaseResponse, IListItem, ISwapQualityIssue } from '@nkr/models/index';
import { SwapQualityIssueListType, SwapQualityIssueReportedBy, TransportMethod } from '@nkr/enums/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Label, Row } from 'react-bootstrap';
import { DropEvent, FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import { SwapQualityIssueWrapper } from '../styles';
import { IValidation } from './validation';
import { IPhoto } from './photo';
import SwapInfo from './swap-info';
import DonorInfo from './donor-info';
import RecipientInfo from './recipient-info';
import FileUpload from '@nkr/components/file-upload';
import RecipientCenterQuestions from './recipient-center-questions';

const initialValidation: IValidation = {
    selectedTypes: true,
    donorWhyWasKidneyLate: true,
    issueDescription: true,
    rootCauses: true,
    correctiveAction: true,
    didSurgeonDiscuss: true,
    preSurgicalDiscussionDetails: true,
    postSurgicalDiscussionDetails: true,
    recipientHaveOrganQualityConcerns: true,
    recipientOrganQualityConcernDetails: true,
    recipientDidOrganFunctionImmediately: true,
    recipientIsOrganCurrentlyFunctioning: true,
    requestInvestigation: true,
    kidneyReplacementRequest: true
};

interface IProps {
    centerId: number,
    isAdmin: boolean,
    viewOnly: boolean
}

interface IStateProps {
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getById: (id: number, centerId: number) => Promise<ISwapQualityIssueResponse>,
    getLookupData: () => Promise<ISwapQualityLookupDataResponse>,
    save: (issueId: number, centerId: number, request: ISwapQualityIssueRequest) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps

interface IState {
    data: ISwapQualityIssue,
    selectedTypes: number[],
    requestInvestigation: number,
    kidneyReplacementRequest: number,
    donorWhyWasKidneyLate: string,
    issueDescription: string,
    rootCauses: string,
    correctiveAction: string,
    surgicalAssistants: string,
    didSurgeonDiscuss: number,
    preSurgicalDiscussionDetails: string,
    postSurgicalDiscussionDetails: string,
    recipientHaveOrganQualityConcerns: number,
    recipientOrganQualityConcernDetails: string,
    recipientDidOrganFunctionImmediately: number,
    recipientIsOrganCurrentlyFunctioning: number,
    administrativeNotes: string,
    photos: IPhoto[],
    validation: IValidation,
    dirty: boolean,
    swapQualityIssueTypes: IListItem[],
    previous: SwapQualityIssueListType,
    reportedBy: SwapQualityIssueReportedBy
}

class SwapQualityIssueEdit extends Component<TProps, IState> {
    readonly state: IState = {
        data: null,
        selectedTypes: [],
        requestInvestigation: -1,
        kidneyReplacementRequest: -1,
        donorWhyWasKidneyLate: '',
        issueDescription: '',
        rootCauses: '',
        correctiveAction: '',
        surgicalAssistants: '',
        didSurgeonDiscuss: -1,
        preSurgicalDiscussionDetails: '',
        postSurgicalDiscussionDetails: '',
        recipientHaveOrganQualityConcerns: -1,
        recipientOrganQualityConcernDetails: '',
        recipientDidOrganFunctionImmediately: -1,
        recipientIsOrganCurrentlyFunctioning: -1,
        administrativeNotes: '',
        photos: [],
        validation: initialValidation,
        dirty: false,
        swapQualityIssueTypes: [],
        previous: null,
        reportedBy: null
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let parsed = qs.parse(location.search);
        let id = parsed.id != null ? parseInt(parsed.id) : 0;
        let previous = parsed.previous != null ? parseInt(parsed.previous) : SwapQualityIssueListType.PendingResponse;
        let reportedBy = parsed.reportedBy != null ? parseInt(parsed.reportedBy) : SwapQualityIssueReportedBy.YourCenter;

        this.props.showLoading('Getting data, one moment please...').then(() => {
            Promise.all([
                this.props.getById(id, this.props.centerId),
                this.props.getLookupData()
            ]).then(([issueResponse, lookupDataResponse]) => {
                let data = issueResponse.data.results;
                let selectedTypes = this.getTypes(data);

                let photos: IPhoto[] = [];

                if (data.isRecipientCenter) {
                    photos = [
                        {
                            index: 0, currentFile: data.recipientKidneyPhoto1, newFile: null, remove: false
                        },
                        {
                            index: 1, currentFile: data.recipientKidneyPhoto2, newFile: null, remove: false
                        },
                        {
                            index: 2, currentFile: data.recipientKidneyPhoto3, newFile: null, remove: false
                        },
                    ];
                } else {
                    photos = [
                        {
                            index: 0, currentFile: data.donorKidneyPhoto1, newFile: null, remove: false
                        },
                        {
                            index: 1, currentFile: data.donorKidneyPhoto2, newFile: null, remove: false
                        },
                        {
                            index: 2, currentFile: data.donorKidneyPhoto3, newFile: null, remove: false
                        },
                    ];
                }

                this.setState({
                    data,
                    selectedTypes,
                    requestInvestigation: data.requestInvestigation,
                    donorWhyWasKidneyLate: data.donorWhyWasKidneyLate,
                    issueDescription: data.isRecipientCenter ? data.recipientIssueDescription : data.donorIssueDescription,
                    rootCauses: data.isRecipientCenter ? data.recipientRootCause : data.donorRootCause,
                    correctiveAction: data.isRecipientCenter ? data.recipientCorrectiveAction : data.donorCorrectiveAction,
                    surgicalAssistants: data.isRecipientCenter ? data.recipientSurgicalAssistants : data.donorSurgicalAssistants,
                    didSurgeonDiscuss: data.isRecipientCenter ? data.recipientDidSurgeonDiscussion : data.donorDidSurgeonDiscussion,
                    preSurgicalDiscussionDetails: data.isRecipientCenter ? data.recipientPreSurgicalDiscussionDetails : data.donorPreSurgicalDiscussionDetails,
                    postSurgicalDiscussionDetails: data.isRecipientCenter ? data.recipientPostSurgicalDiscussionDetails : data.donorPostSurgicalDiscussionDetails,
                    recipientHaveOrganQualityConcerns: data.recipientHaveOrganQualityConcerns,
                    recipientOrganQualityConcernDetails: data.recipientOrganQualityConcernDetails,
                    recipientDidOrganFunctionImmediately: data.recipientDidOrganFunctionImmediately,
                    recipientIsOrganCurrentlyFunctioning: data.recipientIsOrganCurrentlyFunctioning,
                    administrativeNotes: data.administrativeNotes,
                    photos,
                    swapQualityIssueTypes: lookupDataResponse.data.results.swapQualityIssueTypes,
                    previous,
                    reportedBy
                });

                this.props.hideLoading();
            });
        });
    }

    getTypes = (data: ISwapQualityIssue) => {
        if (data.isRecipientCenter) {
            return data.recipientSwapQualityIssueTypes;
        } else {
            return data.donorSwapQualityIssueTypes;
        }
    }

    validate = (): IValidation => {
        let { selectedTypes, donorWhyWasKidneyLate, issueDescription, rootCauses, correctiveAction, didSurgeonDiscuss, preSurgicalDiscussionDetails, postSurgicalDiscussionDetails,
            recipientHaveOrganQualityConcerns, recipientOrganQualityConcernDetails, recipientDidOrganFunctionImmediately, recipientIsOrganCurrentlyFunctioning,
            requestInvestigation, kidneyReplacementRequest } = this.state;

        let isDonorSurgery = this.state.selectedTypes.indexOf(2) >= 0;

        let validation = {
            selectedTypes: selectedTypes.length > 0,
            donorWhyWasKidneyLate: this.state.data.isLatePickup && this.state.data.isRecipientCenter == false ? donorWhyWasKidneyLate != '' : true,
            issueDescription: issueDescription != '',
            rootCauses: rootCauses != '',
            correctiveAction: correctiveAction != '',
            didSurgeonDiscuss: didSurgeonDiscuss != -1,
            preSurgicalDiscussionDetails: preSurgicalDiscussionDetails != '',
            postSurgicalDiscussionDetails: postSurgicalDiscussionDetails != '',
            recipientHaveOrganQualityConcerns: this.state.data.isRecipientCenter ? recipientHaveOrganQualityConcerns != -1 : true,
            recipientOrganQualityConcernDetails: this.state.data.isRecipientCenter ? recipientOrganQualityConcernDetails != '' : true,
            recipientDidOrganFunctionImmediately: this.state.data.isRecipientCenter ? recipientDidOrganFunctionImmediately != -1 : true,
            recipientIsOrganCurrentlyFunctioning: this.state.data.isRecipientCenter ? recipientIsOrganCurrentlyFunctioning != -1 : true,
            requestInvestigation: this.state.data.isRecipientCenter && isDonorSurgery ? requestInvestigation != -1 : true,
            kidneyReplacementRequest: this.state.data.isRecipientCenter && this.state.requestInvestigation == 1 ? kidneyReplacementRequest != -1 : true
        };

        return validation;
    }

    getIsValid = () => {
        let { selectedTypes, donorWhyWasKidneyLate, issueDescription, rootCauses, correctiveAction, didSurgeonDiscuss, preSurgicalDiscussionDetails, postSurgicalDiscussionDetails,
            recipientHaveOrganQualityConcerns, recipientOrganQualityConcernDetails, recipientDidOrganFunctionImmediately, recipientIsOrganCurrentlyFunctioning,
            requestInvestigation, kidneyReplacementRequest } = this.state.validation;

        let isValid = selectedTypes && donorWhyWasKidneyLate && issueDescription && rootCauses && correctiveAction && didSurgeonDiscuss
            && preSurgicalDiscussionDetails && postSurgicalDiscussionDetails && recipientHaveOrganQualityConcerns
            && recipientOrganQualityConcernDetails && recipientDidOrganFunctionImmediately && recipientIsOrganCurrentlyFunctioning
            && requestInvestigation && kidneyReplacementRequest;

        return isValid;
    }

    getReturnLink = () => {
        let hrefPrefix = !this.props.isAdmin ? `/center/${this.props.centerId}/action-center/swap-quality-issues/list` : `/admin/swap-quality-issues/list`;

        switch (this.state.previous) {
            case SwapQualityIssueListType.PendingResponse: return `${hrefPrefix}/pending-response?reportedBy=${this.state.reportedBy}`;
            case SwapQualityIssueListType.IssueReportedIncomplete: return `${hrefPrefix}/issue-reported-incomplete?reportedBy=${this.state.reportedBy}`;
            case SwapQualityIssueListType.IssueReportedComplete: return `${hrefPrefix}/issue-reported-complete?reportedBy=${this.state.reportedBy}`;
            case SwapQualityIssueListType.NoIssueReported: return `${hrefPrefix}/no-issue-reported?reportedBy=${this.state.reportedBy}`;
            case SwapQualityIssueListType.All: return hrefPrefix + `${hrefPrefix}/all?reportedBy=${this.state.reportedBy}`;
            default: return `${hrefPrefix}/pending-response?reportedBy=${this.state.reportedBy}`;
        }
    }

    handleTypeClick = (e: FormEvent<HTMLInputElement>) => {
        let value = parseInt(e.currentTarget.value);

        let selectedTypes = [...this.state.selectedTypes];

        if (selectedTypes.indexOf(value) >= 0) {
            selectedTypes = this.state.selectedTypes.filter(t => t != value);
        } else {
            selectedTypes.push(value);
        }

        this.setState({
            selectedTypes
        });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'donorWhyWasKidneyLate') {
            this.setState({
                donorWhyWasKidneyLate: value
            });
        }

        if (name == 'issueDescription') {
            this.setState({
                issueDescription: value
            });
        }

        if (name == 'rootCauses') {
            this.setState({
                rootCauses: value
            });
        }

        if (name == 'correctiveAction') {
            this.setState({
                correctiveAction: value
            });
        }

        if (name == 'surgicalAssistants') {
            this.setState({
                surgicalAssistants: value
            });
        }

        if (name == 'preSurgicalDiscussionDetails') {
            this.setState({
                preSurgicalDiscussionDetails: value
            });
        }

        if (name == 'postSurgicalDiscussionDetails') {
            this.setState({
                postSurgicalDiscussionDetails: value
            });
        }

        if (name == 'recipientOrganQualityConcernDetails') {
            this.setState({
                recipientOrganQualityConcernDetails: value
            });
        }

        if (name == 'administrativeNotes') {
            this.setState({
                administrativeNotes: value
            });
        }

        if (name == 'requestInvestigation') {
            this.setState({
                requestInvestigation: parseInt(value)
            });
        }

        if (name == 'kidneyReplacementRequest') {
            this.setState({
                kidneyReplacementRequest: parseInt(value)
            });
        }

        if (name == 'didSurgeonDiscuss') {
            this.setState({
                didSurgeonDiscuss: parseInt(value)
            });
        }

        if (name == 'recipientHaveOrganQualityConcerns') {
            this.setState({
                recipientHaveOrganQualityConcerns: parseInt(value)
            });
        }

        if (name == 'recipientDidOrganFunctionImmediately') {
            this.setState({
                recipientDidOrganFunctionImmediately: parseInt(value)
            });
        }

        if (name == 'recipientIsOrganCurrentlyFunctioning') {
            this.setState({
                recipientIsOrganCurrentlyFunctioning: parseInt(value)
            });
        }
    }

    handleSelectFile = (index: number, acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        let photos = _.cloneDeep(this.state.photos);
        let photo = photos.find(x => x.index == index);

        photo.newFile = acceptedFiles[0];

        this.setState({
            photos
        });
    };

    handleRemoveFile = (index: number) => {
        let photos = _.cloneDeep(this.state.photos).map(photo => {
            if (photo.index == index) {
                photo.newFile = null;
            }

            return photo;
        });

        this.setState({
            photos
        });
    };

    handleDeleteOnSave = (index: number) => {
        let photos = _.cloneDeep(this.state.photos).map(photo => {
            if (photo.index == index) {
                photo.remove = true;
            }

            return photo;
        });

        this.setState({
            photos
        });
    }

    handleCancelDeleteOnSave = (index: number) => {
        let photos = _.cloneDeep(this.state.photos).map(photo => {
            if (photo.index == index) {
                photo.remove = false;
            }

            return photo;
        });

        this.setState({
            photos
        });
    }

    handleSave = (e: FormEvent<any>) => {
        let finalizeNow = e.currentTarget.value == '1'

        let validation = { ...this.state.validation };

        if (finalizeNow) {
            validation = this.validate();
        } else {
            validation = initialValidation;
        }

        this.setState({
            validation,
            dirty: true
        }, () => {
            let { selectedTypes, kidneyReplacementRequest, requestInvestigation, issueDescription, rootCauses, correctiveAction, surgicalAssistants, didSurgeonDiscuss,
                preSurgicalDiscussionDetails, postSurgicalDiscussionDetails, recipientHaveOrganQualityConcerns, recipientOrganQualityConcernDetails,
                recipientDidOrganFunctionImmediately, recipientIsOrganCurrentlyFunctioning, donorWhyWasKidneyLate, photos, administrativeNotes } = this.state;

            let request: ISwapQualityIssueRequest = {
                swapQualityIssueTypes: selectedTypes,
                requestInvestigation,
                issueDescription,
                rootCause: rootCauses,
                correctiveAction,
                surgicalAssistants,
                didSurgeonDiscussion: didSurgeonDiscuss,
                preSurgicalDiscussionDetails,
                postSurgicalDiscussionDetails,
                haveOrganQualityConcerns: recipientHaveOrganQualityConcerns,
                organQualityConcernDetails: recipientOrganQualityConcernDetails,
                didOrganFunctionImmediately: recipientDidOrganFunctionImmediately,
                isOrganCurrentlyFunctioning: recipientIsOrganCurrentlyFunctioning,
                doesPatientRequireReplacementKidney: kidneyReplacementRequest,
                donorWhyWasKidneyLate,
                administrativeNotes,
                isFinalized: finalizeNow,

                photo1: photos.length > 0 ? photos[0].newFile : null,
                removePhoto1: photos.length ? photos[0].remove : false,

                photo2: photos.length > 1 ? photos[1].newFile : null,
                removePhoto2: photos.length ? photos[1].remove : false,

                photo3: photos.length > 2 ? photos[2].newFile : null,
                removePhoto3: photos.length ? photos[2].remove : false,
            }

            if (finalizeNow) {
                let isValid = this.getIsValid();

                if (isValid) {
                    this.props.showLoading('Saving and finalizing the swap quality issue, one moment please...').then(() => {
                        this.props.save(this.state.data.id, this.props.centerId, request)
                            .then(response => {
                                if (response.data.success) {
                                    toast.success('Saved successfully', {
                                        onClose: () => {
                                            window.location.href = '/center/' + this.props.centerId + '/action-center/swap-quality-issues/list/pending-response';
                                        }
                                    });
                                } else {
                                    toast.error(response.data.message || 'Failed to save and finalize the swap quality issue');
                                }
                            })
                            .catch(error => {
                                toast.error(error.response?.data?.message || 'An error occurred while saving and finalizing the swap quality issue');
                            })
                            .finally(() => {
                                this.props.hideLoading();
                            });
                    });
                } else {
                    toast.error('Please fill in all required fields before finalizing');
                }
            } else {
                this.props.showLoading('Saving the swap quality issue, one moment please...').then(() => {
                    this.props.save(this.state.data.id, this.props.centerId, request)
                        .then(response => {
                            if (response.data.success) {
                                toast.success('Saved successfully');
                            } else {
                                toast.error(response.data.message || 'Failed to save the swap quality issue');
                            }
                        })
                        .catch(error => {
                            toast.error(error.response?.data?.message || 'An error occurred while saving the swap quality issue');
                        })
                        .finally(() => {
                            this.props.hideLoading();
                            this.getData();
                        });
                });
            }
        });
    }

    render = () => {
        if (this.state.data == null)
            return null;

        const returnLink = this.getReturnLink();
        const isValid = this.getIsValid();
        const disabled = this.props.viewOnly == true;

        return (
            <SwapQualityIssueWrapper className='container' style={{ paddingBottom: 10 }}>
                {(this.state.data.isRecipientCenter && this.state.data.recipientIssuesFinalized) || (this.state.data.isRecipientCenter == false && this.state.data.donorIssuesFinalized) && (
                    <Alert bsStyle='danger' bsSize='sm'>The swap quality issue has been finalized and cannot be edited.</Alert>
                )}

                {!isValid && (
                    <Alert bsStyle='danger'>
                        There are missing required items.  See below...
                    </Alert>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, marginBottom: 20 }}>
                    <h1 className='text-left' style={{ margin: 0 }}>{!disabled ? 'Edit Swap Quality Issue' : 'View Swap Quality Issue'}</h1>
                    <div className='dropdown pull-right'>
                        <a href={returnLink}>Return to {'previous list'}</a>
                    </div>
                </div>

                {!disabled && this.state.data.isLatePickup && (
                    <Alert bsStyle='danger'>
                        The kidney was picked up more than 30 minutes late.  You must fill out a swap quality report.
                    </Alert>
                )}

                <SwapInfo data={this.state.data} />

                <div className='row'>
                    <div className='col-lg-6' style={{ marginLeft: 0 }}>
                        <div className='container-fluid' style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <DonorInfo data={this.state.data} />
                        </div>
                    </div>

                    <div className='col-lg-6' style={{ marginLeft: 0 }}>
                        <div className='container-fluid' style={{ paddingLeft: 0, paddingRight: 0 }}>
                            <RecipientInfo data={this.state.data} />
                        </div>
                    </div>
                </div>

                <FormGroup validationState={this.state.dirty && this.state.validation.selectedTypes == false ? 'error' : null}>
                    <ControlLabel>Swap Quality Issue Types</ControlLabel>
                    <div>
                        {this.state.swapQualityIssueTypes.map(item => {
                            let checked = this.state.selectedTypes.find(t => t == item.id) != null;

                            return (
                                <label key={'t-' + item.id} className='checkbox-inline'>
                                    <input
                                        type='checkbox'
                                        id={'swapQualityIssueType-' + item.id}
                                        name={'swapQualityIssueType-' + item.id}
                                        disabled={disabled}
                                        value={item.id}
                                        checked={checked}
                                        onClick={this.handleTypeClick} /> {item.description}
                                </label>
                            );
                        })}
                    </div>

                    {this.state.dirty && this.state.validation.selectedTypes == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                {this.state.data.isRecipientCenter && this.state.selectedTypes.indexOf(2) >= 0 && (
                    <>
                        <FormGroup validationState={this.state.dirty && this.state.validation.requestInvestigation == false ? 'error' : null}>
                            <ControlLabel>Do you want the donor surgery issue investigated?</ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='requestInvestigation'
                                style={{ width: 'auto' }}
                                disabled={disabled}
                                value={this.state.requestInvestigation}
                                onChange={this.handleFieldChange}
                            >
                                <option value={-1}>- Select -</option>
                                <option value={1}>Yes</option>
                                <option value={2}>No</option>
                            </FormControl>

                            {this.state.dirty && this.state.validation.requestInvestigation == false && (
                                <HelpBlock>
                                    This is required
                                </HelpBlock>
                            )}
                        </FormGroup>

                        {this.state.requestInvestigation == 1 && (
                            <FormGroup validationState={this.state.dirty && this.state.validation.kidneyReplacementRequest == false ? 'error' : null}>
                                <ControlLabel>
                                    Would you like the Medical Board to review this case as part of the 90-Day Kidney Replacement Policy?
                                </ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='kidneyReplacementRequest'
                                    style={{ width: 'auto' }}
                                    disabled={disabled}
                                    onChange={this.handleFieldChange}
                                >
                                    <option value={0}>- Select -</option>
                                    <option value={1}>Yes</option>
                                    <option value={2}>No</option>
                                </FormControl>

                                {this.state.dirty && this.state.validation.kidneyReplacementRequest == false && (
                                    <HelpBlock>
                                        This is required
                                    </HelpBlock>
                                )}
                            </FormGroup>
                        )}
                    </>
                )}

                {this.state.data.isLatePickup && this.state.data.transportMethodId != TransportMethod.Internal && (
                    <FormGroup validationState={this.state.dirty && this.state.validation.donorWhyWasKidneyLate == false ? 'error' : null}>
                        <ControlLabel>
                            Why was the kidney late being picked up?
                        </ControlLabel>
                        <FormControl
                            componentClass='textarea'
                            name='donorWhyWasKidneyLate'
                            style={{ resize: 'none' }}
                            cols={150}
                            rows={3}
                            placeholder='Please write a short description for the reason. If there is nothing to report, type "N/A."'
                            disabled={disabled}
                            value={this.state.donorWhyWasKidneyLate}
                            onChange={this.handleFieldChange} />

                        {this.state.dirty && this.state.validation.donorWhyWasKidneyLate == false && (
                            <HelpBlock>
                                This is required
                            </HelpBlock>
                        )}
                    </FormGroup>
                )}

                <FormGroup validationState={this.state.dirty && this.state.validation.issueDescription == false ? 'error' : null}>
                    <ControlLabel>
                        Describe the swap quality issue(s)
                    </ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='issueDescription'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please write a short description of the issue or issues. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.issueDescription}
                        onChange={this.handleFieldChange} />

                    {this.state.dirty && this.state.validation.issueDescription == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                <FormGroup validationState={this.state.dirty && this.state.validation.rootCauses == false ? 'error' : null}>
                    <ControlLabel>
                        Root Cause(s)
                    </ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='rootCauses'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please write a short description of the cause or causes. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.rootCauses}
                        onChange={this.handleFieldChange} />

                    {this.state.dirty && this.state.validation.rootCauses == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                <FormGroup validationState={this.state.dirty && this.state.validation.correctiveAction == false ? 'error' : null}>
                    <ControlLabel>
                        Corrective Action(s)
                    </ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='correctiveAction'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please write a short description of the actions. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.correctiveAction}
                        onChange={this.handleFieldChange} />

                    {this.state.dirty && this.state.validation.correctiveAction == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                <FormGroup>
                    <ControlLabel>Were there any additional surgeons assisting? <span style={{ fontWeight: 400 }}>(if yes, please list them here)</span></ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='surgicalAssistants'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please specify the additional surgeons. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.surgicalAssistants}
                        onChange={this.handleFieldChange} />
                </FormGroup>

                <FormGroup validationState={this.state.dirty && this.state.validation.didSurgeonDiscuss == false ? 'error' : null}>
                    <ControlLabel>Did your surgeon discuss the case with the other center?</ControlLabel>
                    <FormControl
                        componentClass='select'
                        name='didSurgeonDiscuss'
                        style={{ width: 'auto' }}
                        disabled={disabled}
                        value={this.state.didSurgeonDiscuss}
                        onChange={this.handleFieldChange}
                    >
                        <option value={-1}>- Select -</option>
                        <option value={1}>Yes</option>
                        <option value={2}>No</option>
                    </FormControl>

                    {this.state.dirty && this.state.validation.didSurgeonDiscuss == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                <FormGroup validationState={this.state.dirty && this.state.validation.preSurgicalDiscussionDetails == false ? 'error' : null}>
                    <ControlLabel>Pre-surgery Communication Details</ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='preSurgicalDiscussionDetails'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please write a short description of the details. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.preSurgicalDiscussionDetails}
                        onChange={this.handleFieldChange} />

                    {this.state.dirty && this.state.validation.preSurgicalDiscussionDetails == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                <FormGroup validationState={this.state.dirty && this.state.validation.postSurgicalDiscussionDetails == false ? 'error' : null}>
                    <ControlLabel>Post-surgery Communication Details</ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='postSurgicalDiscussionDetails'
                        style={{ resize: 'none' }}
                        cols={150}
                        rows={3}
                        placeholder='Please write a short description of the details. If there is nothing to report, type "N/A."'
                        disabled={disabled}
                        value={this.state.postSurgicalDiscussionDetails}
                        onChange={this.handleFieldChange} />

                    {this.state.dirty && this.state.validation.postSurgicalDiscussionDetails == false && (
                        <HelpBlock>
                            This is required
                        </HelpBlock>
                    )}
                </FormGroup>

                {this.state.data.isRecipientCenter && (
                    <RecipientCenterQuestions
                        recipientHaveOrganQualityConcerns={this.state.recipientHaveOrganQualityConcerns}
                        recipientOrganQualityConcernDetails={this.state.recipientOrganQualityConcernDetails}
                        recipientDidOrganFunctionImmediately={this.state.recipientDidOrganFunctionImmediately}
                        recipientIsOrganCurrentlyFunctioning={this.state.recipientIsOrganCurrentlyFunctioning}
                        validation={this.state.validation}
                        dirty={this.state.dirty}
                        viewOnly={this.props.viewOnly}
                        onFieldChange={this.handleFieldChange} />
                )}

                <FormGroup>
                    <ControlLabel>Photos</ControlLabel>
                    <div style={{ padding: 5, border: '1px solid #ccc', borderRadius: 4 }}>
                        {this.state.photos.map(photo =>
                            <div style={{ display: 'flex', marginBottom: 10 }}>
                                <div style={{ marginRight: 10, paddingTop: 7 }}>Photo {photo.index + 1}:</div>
                                <div>
                                    {photo.currentFile != null && (
                                        <div className={photo.remove ? 'current-image delete-on-save' : 'current-image'}>
                                            <img src={__API__ + '/swap-quality-issue/photo?fullPath=' + photo.currentFile} style={{ width: 400 }} />
                                        </div>
                                    )}

                                    {photo.currentFile == null && disabled && (
                                        <div style={{ paddingTop: 5 }}><Label>No photo uploaded</Label></div>
                                    )}

                                    {!disabled && (
                                        <FileUpload
                                            label={
                                                <>
                                                    <div style={{ paddingTop: 5, paddingBottom: 5 }}>Click here to select image file or drag &amp; drop the image file here...</div>
                                                    <Alert bsStyle='warning' style={{ paddingTop: 5, paddingBottom: 5, marginBottom: 5 }}>Only JPG, PNG, and GIF files are accepted</Alert>
                                                </>
                                            }
                                            files={photo.newFile != null ? [photo.newFile] : []}
                                            height='100%'
                                            accept={['.jpg', '.png', '.gif']}
                                            singleUploadOnly={true}
                                            onFileDropped={(acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => this.handleSelectFile(photo.index, acceptedFiles, fileRejections, event)}
                                            onRemoveFile={() => this.handleRemoveFile(photo.index)}
                                        />
                                    )}
                                </div>
                                {photo.currentFile != null && (
                                    <div>
                                        <div>
                                            {photo.remove == false ?
                                                <Button bsStyle='danger' disabled={disabled} onClick={() => this.handleDeleteOnSave(photo.index)}>Delete on Save</Button>
                                                :
                                                <Button disabled={disabled} onClick={() => this.handleCancelDeleteOnSave(photo.index)}>Cancel Delete on Save</Button>
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </FormGroup>

                <FormGroup>
                    <ControlLabel>Admin Notes</ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='administrativeNotes'
                        cols={150}
                        rows={3}
                        style={{ resize: 'vertical', minHeight: 100 }}
                        disabled={disabled}
                        value={this.state.administrativeNotes}
                        onChange={this.handleFieldChange} />
                </FormGroup>

                <Row>
                    <Col lg={12} style={{ textAlign: 'center' }}>
                        {!disabled ?
                            <>
                                <Button value='1' bsStyle='primary' onClick={this.handleSave}>Save and Finalize Now</Button>
                                {' '}
                                <Button value='0' onClick={this.handleSave}>Save and Finalize Later</Button>
                                {' '}
                                <a className='btn btn-link btn-large' href={returnLink}>Return without Saving</a>
                            </>
                            :
                            <a className='btn btn-link btn-large' href={returnLink}>Return to List</a>
                        }
                    </Col>
                </Row>
            </SwapQualityIssueWrapper>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getById: (id, centerId) => {
            return dispatch(SwapQualityIssueActionCreators.getById(id, centerId));
        },
        getLookupData: () => {
            return dispatch(SwapQualityIssueActionCreators.getLookupData());
        },
        save: (issueId, centerId, request) => {
            return dispatch(SwapQualityIssueActionCreators.save(issueId, centerId, request));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(SwapQualityIssueEdit)